import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { StructuredData } from "@/components/StructuredData";
import { RocSignalLabel } from "@/components/roc/RocSignalLabel";
import { ArrowRight, MessageSquare, Search, Settings, Globe, ShoppingCart, Sparkles, BarChart3, ShieldCheck } from "lucide-react";

import { Link } from "react-router-dom";
import { usePageSeo } from "@/hooks/usePageSeo";
import { esRoutes, enRoutes } from "@/utils/routes";

interface Faq { question: string; answer: string; }
interface Group {
  title: string;
  icon: any;
  intro: string;
  faqs: Faq[];
  link: { to: string; label: string };
}

const FAQPage = () => {
  const { language, url, onLanguageChange } = usePageSeo();
  const routes = language === "es" ? esRoutes : enRoutes;

  const groups: Group[] = language === "es" ? [
    {
      title: "Cómo funciona trabajar conmigo",
      icon: MessageSquare,
      intro: "Preguntas sobre el proceso de una consultoría de SEO senior, de principio a fin.",
      link: { to: routes.serviceConsultant, label: "Ver qué hace un consultor SEO senior" },
      faqs: [
        {
          question: "¿Cómo empieza un proyecto de SEO contigo?",
          answer: "Normalmente empieza con un mensaje contándome tu sitio, tu mercado y el problema que quieres resolver. A partir de ahí evaluamos si un diagnóstico inicial o una conversación tiene sentido antes de definir el alcance del trabajo."
        },
        {
          question: "¿Trabajas como consultor independiente o como agencia?",
          answer: "Trabajo como consultor independiente. Eso significa que la persona que analiza tu sitio y define la estrategia es la misma que da seguimiento al proyecto, sin capas intermedias de cuentas junior."
        },
        {
          question: "¿Con qué tipo de empresas trabajas normalmente?",
          answer: "Principalmente con empresas B2B, marcas de ecommerce y negocios que atienden al mercado hispano de Estados Unidos, ya sea en inglés, en español o en ambos idiomas."
        },
        {
          question: "¿Ofreces proyectos puntuales o solo retainers mensuales?",
          answer: "Depende del alcance: algunos proyectos son de duración definida (por ejemplo, un diagnóstico o una migración) y otros requieren acompañamiento continuo. Lo definimos según el objetivo real del negocio, no según un paquete fijo."
        }
      ]
    },
    {
      title: "Qué incluye un diagnóstico SEO",
      icon: Search,
      intro: "Qué revisa realmente un diagnóstico antes de proponer una estrategia.",
      link: { to: routes.serviceAudit, label: "Ver el servicio de diagnóstico SEO" },
      faqs: [
        {
          question: "¿Qué se revisa en un diagnóstico SEO?",
          answer: "Un diagnóstico serio revisa arquitectura del sitio, indexación, rendimiento técnico, calidad y estructura del contenido frente a la intención de búsqueda, señales de autoridad, y cómo compite el sitio frente a competidores reales en tu mercado."
        },
        {
          question: "¿El diagnóstico ya incluye la implementación de los cambios?",
          answer: "No necesariamente. El diagnóstico entrega un análisis priorizado de qué corregir y por qué. La implementación puede ser interna, con tu equipo de desarrollo, o como un proyecto aparte, según lo que decidas."
        },
        {
          question: "¿Sirve un diagnóstico si ya tengo una agencia de SEO?",
          answer: "Sí, es habitual pedir una segunda opinión externa cuando los resultados de una agencia no son claros o cuando quieres validar la estrategia actual antes de renovar un contrato."
        }
      ]
    },
    {
      title: "SEO técnico frente a estrategia de contenido",
      icon: Settings,
      intro: "Cómo se relacionan la parte técnica y la parte de contenido/estrategia dentro de un mismo proyecto.",
      link: { to: routes.serviceTechnical, label: "Ver el servicio de SEO técnico" },
      faqs: [
        {
          question: "¿Cuál es la diferencia entre SEO técnico y estrategia de contenido?",
          answer: "El SEO técnico se ocupa de que Google pueda rastrear, indexar y entender el sitio correctamente (velocidad, arquitectura, código, señales estructuradas). La estrategia de contenido decide qué temas cubrir y cómo responder a la intención de búsqueda. Un sitio necesita ambos para posicionar de forma sostenible."
        },
        {
          question: "¿Necesito arreglar primero lo técnico o el contenido?",
          answer: "Depende del diagnóstico. Si hay problemas de indexación o rastreo, normalmente eso se prioriza porque limita el impacto de cualquier trabajo de contenido posterior. Si la base técnica ya es sólida, el foco se mueve hacia contenido y estructura."
        },
        {
          question: "¿Trabajas junto al equipo de desarrollo de mi empresa?",
          answer: "Sí. En proyectos técnicos suelo coordinar directamente con el equipo de desarrollo o la agencia web para priorizar cambios sin bloquear otros trabajos en el roadmap del producto."
        }
      ]
    },
    {
      title: "Búsqueda internacional y mercado hispano/bilingüe",
      icon: Globe,
      intro: "Preguntas sobre expandir la visibilidad de un sitio a otros países, idiomas o al mercado hispano de EE. UU.",
      link: { to: routes.serviceInternational, label: "Ver el servicio de SEO internacional" },
      faqs: [
        {
          question: "¿Traducir mi sitio a otro idioma ya es SEO internacional?",
          answer: "No. Traducir el contenido es solo un paso. El SEO internacional también requiere definir la estructura de URLs por país o idioma, configurar señales como hreflang, e investigar palabras clave propias de cada mercado en lugar de traducir literalmente las existentes."
        },
        {
          question: "¿Cómo abordas el SEO para negocios que atienden al mercado hispano de EE. UU.?",
          answer: "El mercado hispano de EE. UU. no es un mercado 'traducido' del inglés: tiene su propio comportamiento de búsqueda, términos y competencia. Trabajo la investigación de palabras clave, la estructura del sitio y el contenido pensando en ese comportamiento específico, en español, en inglés o en ambos según cómo busque tu audiencia."
          ,
        },
        {
          question: "¿Un mismo sitio puede posicionar bien en inglés y en español a la vez?",
          answer: "Sí, siempre que la arquitectura del sitio separe correctamente las versiones por idioma y cada una tenga su propia estrategia de contenido y palabras clave, en lugar de duplicar el mismo texto traducido."
        }
      ]
    },
    {
      title: "Ecommerce y Amazon",
      icon: ShoppingCart,
      intro: "SEO para tiendas online y para marcas que venden en Amazon.",
      link: { to: routes.serviceEcommerce, label: "Ver el servicio de SEO para ecommerce" },
      faqs: [
        {
          question: "¿El SEO de ecommerce es distinto al SEO de un sitio de servicios?",
          answer: "Sí. En ecommerce hay retos particulares como categorías con paginación, filtros que generan URLs duplicadas, productos que salen y entran de inventario, y la necesidad de que tanto categorías como fichas de producto posicionen para intenciones distintas."
        },
        {
          question: "¿Trabajas SEO de Amazon además del sitio propio?",
          answer: "Sí, de forma separada. El posicionamiento dentro de Amazon (listings, términos de búsqueda, estructura de catálogo) funciona con reglas distintas a Google, aunque ambas estrategias pueden reforzarse cuando la marca vende en los dos canales."
        },
        {
          question: "¿Puedo tener SEO en Google y en Amazon a la vez con una sola estrategia?",
          answer: "Puedes tener una estrategia de marca coherente, pero la ejecución es distinta en cada plataforma porque los algoritmos, las señales y el comportamiento de búsqueda no son los mismos."
        }
      ]
    },
    {
      title: "Visibilidad en búsqueda con IA (ChatGPT, Gemini, AI Overviews)",
      icon: Sparkles,
      intro: "Cómo se relaciona el SEO tradicional con aparecer en respuestas generadas por IA.",
      link: { to: routes.serviceLlm, label: "Ver el servicio de posicionamiento en IA" },
      faqs: [
        {
          question: "¿El SEO tradicional sirve para aparecer en respuestas de IA?",
          answer: "Es la base, pero no es suficiente por sí solo. Los sistemas de IA generativa priorizan contenido claro, bien estructurado y con información verificable, así que muchas de las buenas prácticas técnicas y de contenido ayudan, pero conviene revisar también cómo se estructura la información para que sea fácil de citar."
        },
        {
          question: "¿Puedes garantizar que mi marca aparezca en ChatGPT o en AI Overviews?",
          answer: "No. Nadie puede garantizar apariciones específicas en sistemas de IA, igual que nadie puede garantizar una posición exacta en Google. Lo que sí se puede hacer es optimizar la estructura, claridad y autoridad del contenido para mejorar la probabilidad de ser citado."
        },
        {
          question: "¿Esto reemplaza al SEO tradicional?",
          answer: "No, lo complementa. La búsqueda tradicional en Google sigue siendo relevante para la mayoría de negocios, y la visibilidad en IA se construye sobre los mismos fundamentos de contenido de calidad y estructura técnica sólida."
        }
      ]
    },
    {
      title: "Medición y reportes",
      icon: BarChart3,
      intro: "Cómo se hace seguimiento del progreso de un proyecto de SEO.",
      link: { to: routes.methodology, label: "Ver la metodología de trabajo" },
      faqs: [
        {
          question: "¿Cómo se mide el progreso de un proyecto de SEO?",
          answer: "El seguimiento combina métricas técnicas (indexación, errores de rastreo, Core Web Vitals), métricas de visibilidad (posiciones y tráfico orgánico por segmento) y, cuando es posible conectarlo, el impacto en leads o ventas atribuibles al canal orgánico."
        },
        {
          question: "¿Con qué frecuencia se revisan los resultados?",
          answer: "Depende del alcance del proyecto y se acuerda al inicio. Proyectos de acompañamiento continuo suelen tener revisiones periódicas; proyectos puntuales como un diagnóstico entregan sus hallazgos al finalizar el análisis."
        }
      ]
    },
    {
      title: "Qué NO se promete",
      icon: ShieldCheck,
      intro: "Para evaluar bien una consultoría de SEO conviene saber también qué no forma parte de la oferta.",
      link: { to: routes.contact, label: "Escríbeme para hablar de tu caso" },
      faqs: [
        {
          question: "¿Ofreces garantía de posición número uno en Google?",
          answer: "No. Ningún consultor u agencia que entienda cómo funciona la búsqueda puede garantizar una posición específica, porque Google no vende ni controla ese resultado de forma directa para terceros."
        },
        {
          question: "¿Prometes un tiempo de respuesta fijo para resultados o para mensajes?",
          answer: "No se ofrecen plazos fijos de resultados ni compromisos de tiempo de respuesta. El SEO depende de factores fuera del control de cualquier consultor, como cambios de algoritmo o el nivel de competencia del sector."
        },
        {
          question: "¿Usas técnicas de enlaces masivos o de bajo costo?",
          answer: "No. No se trabaja con paquetes de enlaces baratos ni con tácticas que puedan poner en riesgo el sitio a mediano plazo. El enfoque es construir autoridad y visibilidad de forma sostenible."
        }
      ]
    }
  ] : [
    {
      title: "How working together actually works",
      icon: MessageSquare,
      intro: "Questions about the process of a senior SEO engagement, start to finish.",
      link: { to: routes.serviceConsultant, label: "See what a senior SEO consultant does" },
      faqs: [
        {
          question: "How does a project with you typically start?",
          answer: "It usually starts with a message about your site, your market, and the problem you're trying to solve. From there we figure out whether an initial diagnosis or a conversation makes sense before scoping the work."
        },
        {
          question: "Do you work as an independent consultant or an agency?",
          answer: "I work as an independent consultant. That means the person who analyzes your site and defines the strategy is the same person following through on the project, without layers of junior account managers in between."
        },
        {
          question: "What kind of companies do you usually work with?",
          answer: "Mainly B2B companies, ecommerce brands, and businesses serving the US Hispanic market, whether in English, Spanish, or both."
        },
        {
          question: "Do you offer one-off projects or only monthly retainers?",
          answer: "It depends on the scope: some projects have a defined duration (a diagnosis or a migration, for example), and others need ongoing support. We define that based on the actual business goal, not a fixed package."
        }
      ]
    },
    {
      title: "What an SEO diagnosis actually includes",
      icon: Search,
      intro: "What a diagnosis really reviews before any strategy gets proposed.",
      link: { to: routes.serviceAudit, label: "See the SEO diagnosis service" },
      faqs: [
        {
          question: "What gets reviewed in an SEO diagnosis?",
          answer: "A serious diagnosis reviews site architecture, indexation, technical performance, content quality and structure against search intent, authority signals, and how the site competes against real competitors in your market."
        },
        {
          question: "Does the diagnosis already include implementing the changes?",
          answer: "Not necessarily. The diagnosis delivers a prioritized analysis of what to fix and why. Implementation can happen internally, with your dev team, or as a separate project, depending on what you decide."
        },
        {
          question: "Is a diagnosis useful if I already have an SEO agency?",
          answer: "Yes — it's common to want an outside second opinion when an agency's results aren't clear, or to validate the current strategy before renewing a contract."
        }
      ]
    },
    {
      title: "Technical SEO vs. content strategy",
      icon: Settings,
      intro: "How the technical side and the content/strategy side fit together within one project.",
      link: { to: routes.serviceTechnical, label: "See the technical SEO service" },
      faqs: [
        {
          question: "What's the difference between technical SEO and content strategy?",
          answer: "Technical SEO makes sure Google can crawl, index and understand the site correctly (speed, architecture, code, structured signals). Content strategy decides which topics to cover and how to answer search intent. A site needs both to rank sustainably."
        },
        {
          question: "Should I fix technical issues first, or content first?",
          answer: "It depends on the diagnosis. If there are indexation or crawling issues, those are usually prioritized because they limit the impact of any content work that follows. If the technical foundation is already solid, the focus shifts to content and structure."
        },
        {
          question: "Do you work directly with my company's development team?",
          answer: "Yes. On technical projects I usually coordinate directly with the development team or web agency to prioritize changes without blocking other items on the product roadmap."
        }
      ]
    },
    {
      title: "International search and the bilingual/Hispanic market",
      icon: Globe,
      intro: "Questions about expanding a site's visibility to other countries, languages, or the US Hispanic market.",
      link: { to: routes.serviceInternational, label: "See the international SEO service" },
      faqs: [
        {
          question: "Is translating my site into another language already international SEO?",
          answer: "No. Translating content is just one step. International SEO also requires defining a URL structure by country or language, setting up signals like hreflang, and researching keywords specific to each market instead of literally translating the existing ones."
        },
        {
          question: "How do you approach SEO for businesses serving the US Hispanic market?",
          answer: "The US Hispanic market isn't a 'translated' version of the English market — it has its own search behavior, terminology, and competition. I approach keyword research, site structure, and content with that specific behavior in mind, in Spanish, English, or both depending on how your audience actually searches."
        },
        {
          question: "Can the same site rank well in English and Spanish at once?",
          answer: "Yes, as long as the site architecture correctly separates the language versions and each one has its own content and keyword strategy, instead of duplicating the same translated text."
        }
      ]
    },
    {
      title: "Ecommerce and Amazon",
      icon: ShoppingCart,
      intro: "SEO for online stores and for brands that sell on Amazon.",
      link: { to: routes.serviceEcommerce, label: "See the ecommerce SEO service" },
      faqs: [
        {
          question: "Is ecommerce SEO different from SEO for a services site?",
          answer: "Yes. Ecommerce has particular challenges like paginated categories, filters that generate duplicate URLs, products going in and out of stock, and the need for both category and product pages to rank for different intents."
        },
        {
          question: "Do you handle Amazon SEO in addition to a brand's own site?",
          answer: "Yes, as a separate track. Ranking inside Amazon (listings, search terms, catalog structure) follows different rules than Google, though both strategies can reinforce each other when a brand sells through both channels."
        },
        {
          question: "Can I have Google SEO and Amazon SEO under one strategy?",
          answer: "You can have a coherent brand strategy, but execution differs on each platform because the algorithms, signals, and search behavior aren't the same."
        }
      ]
    },
    {
      title: "Visibility in AI search (ChatGPT, Gemini, AI Overviews)",
      icon: Sparkles,
      intro: "How traditional SEO relates to showing up in AI-generated answers.",
      link: { to: routes.serviceLlm, label: "See the AI search visibility service" },
      faqs: [
        {
          question: "Does traditional SEO help you show up in AI answers?",
          answer: "It's the foundation, but it's not enough on its own. Generative AI systems prioritize content that's clear, well structured, and verifiable, so many technical and content best practices help — but it's worth reviewing how information is structured so it's easy to cite."
        },
        {
          question: "Can you guarantee my brand will show up in ChatGPT or AI Overviews?",
          answer: "No. Nobody can guarantee specific appearances in AI systems, the same way nobody can guarantee an exact Google ranking. What can be done is optimizing content structure, clarity and authority to improve the odds of being cited."
        },
        {
          question: "Does this replace traditional SEO?",
          answer: "No, it complements it. Traditional Google search is still relevant for most businesses, and AI visibility is built on the same foundation of quality content and solid technical structure."
        }
      ]
    },
    {
      title: "Measurement and reporting",
      icon: BarChart3,
      intro: "How progress on an SEO project actually gets tracked.",
      link: { to: routes.methodology, label: "See the working methodology" },
      faqs: [
        {
          question: "How is progress on an SEO project measured?",
          answer: "Tracking combines technical metrics (indexation, crawl errors, Core Web Vitals), visibility metrics (rankings and organic traffic by segment), and, when it can be connected, the impact on leads or sales attributable to the organic channel."
        },
        {
          question: "How often are results reviewed?",
          answer: "It depends on the scope of the project and is agreed upfront. Ongoing engagements usually have periodic reviews; one-off projects like a diagnosis deliver findings once the analysis is complete."
        }
      ]
    },
    {
      title: "What is NOT promised",
      icon: ShieldCheck,
      intro: "To properly evaluate an SEO consultant, it also helps to know what's not part of the offer.",
      link: { to: routes.contact, label: "Reach out to discuss your case" },
      faqs: [
        {
          question: "Do you guarantee a #1 ranking on Google?",
          answer: "No. No consultant or agency that understands how search actually works can guarantee a specific ranking, because Google doesn't sell or directly control that outcome for third parties."
        },
        {
          question: "Do you promise a fixed timeline for results or a response time for messages?",
          answer: "No fixed result timelines or response-time commitments are offered. SEO depends on factors outside any consultant's control, such as algorithm changes or how competitive an industry is."
        },
        {
          question: "Do you use mass link-building or low-cost link tactics?",
          answer: "No. There's no work with cheap link packages or tactics that could put a site at risk in the medium term. The focus is building authority and visibility sustainably."
        }
      ]
    }
  ];

  const meta = {
    es: {
      title: "Preguntas frecuentes sobre SEO | Luis ROC, Consultor SEO",
      description: "Respuestas claras sobre diagnóstico SEO, SEO técnico, internacional, bilingüe, ecommerce, Amazon y visibilidad en IA. Sin garantías inventadas.",
      pageTitle: "Preguntas frecuentes sobre SEO",
      subtitle: "Respuestas directas a las preguntas que se hacen las empresas antes de contratar a un consultor SEO senior.",
      contactCTA: "¿No encuentras tu respuesta?",
      contactButton: "Escríbeme directamente"
    },
    en: {
      title: "Frequently Asked Questions about SEO | Luis ROC, SEO Consultant",
      description: "Clear answers about SEO diagnosis, technical SEO, international, bilingual and ecommerce SEO, Amazon, and AI search visibility. No invented guarantees.",
      pageTitle: "Frequently Asked Questions about SEO",
      subtitle: "Direct answers to the questions companies ask before hiring a senior SEO consultant.",
      contactCTA: "Can't find your answer?",
      contactButton: "Reach out directly"
    }
  };

  const currentMeta = { ...meta[language], url };

  const faqSchemaQuestions = groups.flatMap((group) =>
    group.faqs.map((faq) => ({ question: faq.question, answer: faq.answer }))
  );

  const slug = (value: string, index: number) =>
    `faq-${index + 1}-${value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 40)}`;

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>{currentMeta.title}</title>
        <meta name="description" content={currentMeta.description} />
        <link rel="canonical" href={currentMeta.url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={currentMeta.title} />
        <meta property="og:description" content={currentMeta.description} />
        <meta property="og:url" content={currentMeta.url} />
      </Helmet>

      <StructuredData type="faq" data={{ questions: faqSchemaQuestions }} />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={onLanguageChange} />

        <main>
          <section className="border-b border-slate-800 bg-slate-950 py-16 lg:py-24">
            <div className="container-custom">
              <RocSignalLabel index="00" tone="invert">
                {language === 'es' ? 'Índice de conocimiento' : 'Knowledge index'}
              </RocSignalLabel>
              <h1 className="font-display mt-6 max-w-4xl text-3xl font-bold leading-[1.1] tracking-tight text-white lg:text-5xl">
                {currentMeta.pageTitle}
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 lg:text-lg">
                {currentMeta.subtitle}
              </p>
            </div>
          </section>

          <section className="py-14 lg:py-20">
            <div className="container-custom">
              <div className="grid gap-12 lg:grid-cols-[260px_1fr] lg:gap-16">
                <nav
                  aria-label={language === 'es' ? 'Categorías de preguntas' : 'Question categories'}
                  className="min-w-0 lg:sticky lg:top-28 lg:self-start"
                >
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    {language === 'es' ? 'Categorías' : 'Categories'}
                  </p>
                  <ol className="mt-4 border-t border-border">
                    {groups.map((group, gIndex) => (
                      <li key={group.title} className="border-b border-border">
                        <a
                          href={`#${slug(group.title, gIndex)}`}
                          className="flex gap-3 py-3 text-sm text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                        >
                          <span className="font-mono text-[11px] text-brand">
                            {String(gIndex + 1).padStart(2, '0')}
                          </span>
                          <span className="leading-snug">{group.title}</span>
                        </a>
                      </li>
                    ))}
                  </ol>
                </nav>

                <div className="min-w-0 space-y-14">
                  {groups.map((group, gIndex) => (
                    <section key={group.title} id={slug(group.title, gIndex)} className="scroll-mt-28">
                      <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-brand">
                        {String(gIndex + 1).padStart(2, '0')}
                      </p>
                      <h2 className="font-display mt-3 text-xl font-bold tracking-tight text-foreground lg:text-2xl">
                        {group.title}
                      </h2>
                      <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground">{group.intro}</p>

                      <Accordion type="single" collapsible className="mt-6 w-full border-t border-border">
                        {group.faqs.map((faq, index) => (
                          <AccordionItem key={index} value={`item-${gIndex}-${index}`} className="border-b border-border">
                            <AccordionTrigger className="py-5 text-left hover:no-underline focus-visible:ring-2 focus-visible:ring-brand">
                              <span className="text-sm font-semibold text-foreground lg:text-base">{faq.question}</span>
                            </AccordionTrigger>
                            <AccordionContent className="pb-6">
                              <p className="max-w-3xl text-sm leading-relaxed text-muted-foreground">{faq.answer}</p>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>

                      <Link
                        to={group.link.to}
                        className="roc-link mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                      >
                        {group.link.label}
                        <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      </Link>
                    </section>
                  ))}

                  <div className="border border-border p-8 lg:p-10">
                    <h2 className="font-display text-2xl font-bold text-foreground">{currentMeta.contactCTA}</h2>
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground">
                      {language === 'es'
                        ? 'Si no encontraste la respuesta que buscas, escríbeme directamente contándome tu caso.'
                        : "If you didn't find the answer you're looking for, reach out directly and tell me about your case."}
                    </p>
                    <Link
                      to={routes.contact}
                      className="roc-link mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                    >
                      {currentMeta.contactButton}
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer language={language} />
      </div>
    </>
  );
};


export default FAQPage;
